import React, { useState, useEffect, useCallback } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { BREAKPOINTS, CAROUSEL_TIMING } from '../constants/ui';

const GoogleReviewsSection = () => {
  const { language, t } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [slidesPerView, setSlidesPerView] = useState(3);

  const reviews = [
    {
      id: 1, name: 'Evelia Garcia', rating: 5, initials: 'EG', color: 'bg-blue-500',
      date: language === 'en' ? '2 months ago' : 'Hace 2 meses',
      text: language === 'en'
        ? 'The doctor was very attentive and answered all our questions. Thank you so much! The process was very professional and the results exceeded my expectations.'
        : 'La dra. fue muy atenta y contestó todas nuestras preguntas. ¡Muchas gracias dra! El proceso fue muy profesional y los resultados superaron mis expectativas.'
    },
    {
      id: 2, name: 'Alejandra Salcido', rating: 5, initials: 'AS', color: 'bg-pink-500',
      date: language === 'en' ? '3 months ago' : 'Hace 3 meses',
      text: language === 'en'
        ? 'Excellent care, very good results, highly recommended. The medical team is very professional and they make you feel very comfortable throughout the entire process.'
        : 'Excelente atención, muy buenos resultados, súper recomendados. El equipo médico es muy profesional y te hacen sentir muy cómodo durante todo el proceso.'
    },
    {
      id: 3, name: 'Hector Pinon', rating: 5, initials: 'HP', color: 'bg-green-500',
      date: language === 'en' ? '1 month ago' : 'Hace 1 mes',
      text: language === 'en'
        ? 'Excellent professional service from all staff, especially Dr. Cañas. The post-operative follow-up was incredible and the results are very natural.'
        : 'Excelente servicio profesional de todo el personal en especial de la Dra Cañas. El seguimiento post-operatorio fue increíble y los resultados son muy naturales.'
    },
    {
      id: 4, name: 'Roberto Martinez', rating: 5, initials: 'RM', color: 'bg-purple-500',
      date: language === 'en' ? '2 weeks ago' : 'Hace 2 semanas',
      text: language === 'en'
        ? 'After researching many clinics, I chose Bianhi and it was the best decision. The lifetime guarantee gave me a lot of confidence. The results speak for themselves.'
        : 'Después de investigar muchas clínicas, elegí Bianhi y fue la mejor decisión. La garantía de por vida me dio mucha confianza. Los resultados hablan por sí solos.'
    },
    {
      id: 5, name: 'Carlos Mendoza', rating: 5, initials: 'CM', color: 'bg-orange-500',
      date: language === 'en' ? '4 months ago' : 'Hace 4 meses',
      text: language === 'en'
        ? 'Incredible experience from start to finish. Dr. Parra and his team are true experts. My hair looks completely natural and I am very happy with the results.'
        : 'Increíble experiencia de principio a fin. El Dr. Parra y su equipo son verdaderos expertos. Mi cabello luce completamente natural y estoy muy feliz con los resultados.'
    },
    {
      id: 6, name: 'Miguel Angel Torres', rating: 5, initials: 'MT', color: 'bg-teal-500',
      date: language === 'en' ? '3 weeks ago' : 'Hace 3 semanas',
      text: language === 'en'
        ? 'I had a beard graft and was impressed with the result. The personalized attention is what I highlight the most. They only treat one patient per day.'
        : 'Me hice el injerto de barba y quedé impresionado con el resultado. La atención personalizada es lo que más destaco. Solo atienden a un paciente por día.'
    }
  ];

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < BREAKPOINTS.MOBILE) setSlidesPerView(1);
      else if (window.innerWidth < BREAKPOINTS.DESKTOP) setSlidesPerView(2);
      else setSlidesPerView(3);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, reviews.length - slidesPerView);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  }, [maxIndex]);

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => { nextSlide(); }, CAROUSEL_TIMING.AUTO_SCROLL_INTERVAL);
    return () => clearInterval(interval);
  }, [isPaused, nextSlide]);

  return (
    <section 
      className="py-20 md:py-28 bg-white" 
      data-testid="reviews-section"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-sm font-semibold tracking-wider uppercase text-[#009d9f] mb-4">
            {t('reviews.tagline')}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('reviews.title')}{' '}
            <span className="text-[#009d9f]">{t('reviews.titleHighlight')}</span>
          </h2>
          
          <div className="inline-flex items-center gap-4 bg-white rounded-2xl px-6 py-4 shadow-lg border border-slate-100" data-testid="google-rating-badge">
            <img src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" alt="Google" className="h-6 w-auto" />
            <div className="h-8 w-px bg-slate-200" />
            <div className="flex items-center gap-2">
              <span className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>5.0</span>
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={`star-rating-${i}`} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-slate-700">100+ {t('reviews.opinions')}</p>
              <p className="text-xs text-slate-500">{t('reviews.onGoogle')}</p>
            </div>
          </div>
        </div>

        <div className="relative" data-testid="reviews-carousel">
          <button onClick={prevSlide} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#009d9f] hover:text-white transition-all border border-slate-200" data-testid="reviews-prev-btn">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button onClick={nextSlide} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#009d9f] hover:text-white transition-all border border-slate-200" data-testid="reviews-next-btn">
            <ChevronRight className="w-6 h-6" />
          </button>

          <div className="overflow-hidden px-8">
            <div className="flex transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${currentIndex * (100 / slidesPerView)}%)` }}>
              {reviews.map((review, index) => (
                <div key={review.id} className="flex-shrink-0 px-3" style={{ width: `${100 / slidesPerView}%` }}>
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-lg hover:border-[#14B8A6] transition-all duration-300 relative h-full" data-testid={`review-card-${index}`}>
                    <Quote className="absolute top-4 right-4 w-8 h-8 text-[#CCFBF1]" />
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-12 h-12 rounded-full ${review.color} flex items-center justify-center text-white font-bold`}>{review.initials}</div>
                      <div>
                        <h4 className="font-semibold text-slate-900">{review.name}</h4>
                        <p className="text-xs text-slate-500">{review.date}</p>
                      </div>
                    </div>
                    <div className="flex gap-0.5 mb-3">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={`review-star-${review.id}-${i}`} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-slate-600 text-sm leading-relaxed">"{review.text}"</p>
                    <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2">
                      <svg className="w-4 h-4" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      <span className="text-xs text-slate-400">{t('reviews.verified')}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-center gap-2 mt-8">
          {Array.from({ length: maxIndex + 1 }).map((_, index) => (
            <button key={`reviews-dot-${index}`} onClick={() => setCurrentIndex(index)} className={`w-2.5 h-2.5 rounded-full transition-all ${index === currentIndex ? 'bg-[#009d9f] w-8' : 'bg-slate-300 hover:bg-slate-400'}`} data-testid={`reviews-dot-${index}`} />
          ))}
        </div>

        <div className="text-center mt-8">
          <a href="https://www.google.com/search?q=Bianhi+Medical+Hair+Restoration+Tijuana+opiniones" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#009d9f] font-semibold hover:underline" data-testid="view-all-reviews-link">
            {t('reviews.viewAll')}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviewsSection;
